import React from 'react';
import MainLayout from 'components/layout/MainLayout';
import BlogArticle from 'components/blog/BlogArticle';
import { ASSET_PREFIX } from 'utils/constants';
import { NextSeo } from 'next-seo';
import RoundedButton from 'components/RoundedButton';
import Separator from 'components/Separator';
import BlogLink from 'components/blog/BlogLink';
import { getCta } from 'utils/common';

const Article = () => {
  const IMAGE_DIRECTORY = `${ASSET_PREFIX}/images/blog/articles/family-friendly-vacations`;

  return (
    <MainLayout additionalClassName="blogPost">
      <NextSeo
        title="Family-Friendly Vacations & Days Out | Blog | DAYMADE"
        description="Our guide for the perfect family-friendly vacation or days out where you can create unforgettable memories with your kids"
        openGraph={{
          title: 'Family-Friendly Vacations & Days Out | Blog | DAYMADE',
          description:
            'Our guide for the perfect family-friendly vacation or days out where you can create unforgettable memories with your kids',
          type: 'article',
          url: 'https://www.daymade.co.uk/blog/family-friendly-vacations',
          images: [
            {
              url: `${IMAGE_DIRECTORY}/hero.jpg`,
              alt: 'Giraffe Manor',
            },
          ],
        }}
        // additionalMetaTags={[{
        //   name: 'keywords',
        //   content: 'daymade, family, holidays',
        // }]}
      />

      <BlogArticle
        imageDirectory={IMAGE_DIRECTORY}
        heroAltText="Giraffe Manor"
        heroImageYPosition="0%"
      >
        <>
          <h1 style={{ fontSize: '1.6em', lineHeight: '1.4em' }}>
            Family-Friendly Vacations & Days Out
          </h1>

          <p className="subheading">
            Our guide to create unforgettable memories with your kids
          </p>

          <div
            className="separator"
            style={{ width: '80%', marginTop: 0 }}
          />

          <p>
            Are you looking for the perfect family-friendly vacation or day out
            where you can create unforgettable memories with your kids? Look no
            further! In this post, we will take you on a journey to discover
            some destinations and experiences that are guaranteed to please both
            parents and children alike. Get ready for a fun-filled adventure!
          </p>

          <h2>Stay in the Giraffe Manor in Nairobi</h2>

          <p>
            Imagine waking up to the sight of giraffes grazing outside your
            window. At{' '}
            <BlogLink internal="/prizes/details/win-giraffe-manor-stay">
              the Giraffe Manor in Nairobi
            </BlogLink>
            , this dream becomes a reality. The family-friendly accommodation
            offers a once-in-a-lifetime experience for animal lovers. Interact
            with the friendly giraffes, feed them from your breakfast table, and
            learn about their conservation efforts. It's a magical experience
            that your kids will cherish forever. Take up to 5 friends/family
            members for a week in the Giraffe Manor and explore the gorgeous
            surroundings, go on a safari and jump on a helicopter for an
            unforgettable excursion. As this is a once-in-a-lifetime kind of
            trip, we'll also throw in £20k in spending money, so you can truly
            make the most of it!
          </p>

          <h2>7-night Disney Caribbean Cruise</h2>

          <p>
            Embark on a magical journey aboard a{' '}
            <BlogLink internal="/prizes/details/win-disney-cruise-with-family">
              7-night Disney Caribbean Cruise
            </BlogLink>{' '}
            from Texas, heading to Mexico, Grand Cayman, Jamaica and Disney's
            very own private island. With a variety of activities and
            entertainment options for all ages, this family-friendly cruise is
            the perfect way to create lasting memories with your kids. Meet
            beloved Disney characters, enjoy Broadway-style shows, and indulge
            in delicious dining options. From thrilling water slides to kids'
            clubs and family-friendly excursions, there's something for everyone
            on board. Watch Broadway-worthy Disney musicals on board, explore
            the highlights of the Caribbean and enjoy an all-inclusive week
            filled with fun for the whole family. This prize includes return
            tickets for 4 from London to Texas (2 adults, 2 children), a room
            for 4 on board the Disney Cruise and up to £1k spending money.
          </p>

          <h2>Adventure Holiday in North Wales</h2>

          <p>
            For adventurous families,{' '}
            <BlogLink internal="/prizes/details/family-adventure-wales">
              North Wales is the ultimate playground
            </BlogLink>
            . Experience the thrill of canyoning as you navigate through
            stunning gorges and waterfalls. Conquer mountains and enjoy
            breathtaking views while mountaineering. Brave the rapids of
            whitewater rafting and create unforgettable memories together. North
            Wales offers a wide range of outdoor activities that will keep the
            whole family entertained and exhilarated - guaranteed they’ll be
            sleeping all the way home too!
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary2.jpg`}
            alt="A group of friends mountaineering"
            className="singleBlogImage"
          />

          <h2>Aqua Dome Wellness Retreat in Tirol</h2>

          <p>
            If relaxation and wellness are what you seek, head to the{' '}
            <BlogLink internal="/prizes/details/win-aqua-dome-trip">
              Aqua Dome in Tirol, Austria
            </BlogLink>
            . This family-friendly wellness retreat offers a variety of spa
            facilities, including pools, saunas, and relaxation rooms. While you
            unwind and rejuvenate, your kids can enjoy the dedicated children's
            area with water features and slides. It's the perfect balance of
            relaxation and fun for the whole family. And, breaaaathe.
          </p>

          <h2>Port Lympne Safari Stay</h2>

          <p>
            Experience the wonders of the animal kingdom with a safari stay at{' '}
            <BlogLink internal="/prizes/details/port-lympne-2000">
              Port Lympne
            </BlogLink>{' '}
            in the United Kingdom. This family-friendly wildlife reserve offers
            the opportunity to get up close and personal with a variety of
            animals, including giraffes, zebras, rhinos, ostriches and
            wildebeest as well as some of the more elusive antelope herds
            roaming the 100 acre wilderness. Stay in unique glamping tents, and
            enjoy guided tours and animal encounters. It's an unforgettable
            adventure that will ignite your kids' love for wildlife.
          </p>

          <h2>Days Out at Legoland, Thorpe Park or Alton Towers</h2>

          <p>
            For a day filled with thrills and excitement, visit{' '}
            <BlogLink internal="/prizes/details/legoland-day-out">
              Legoland
            </BlogLink>
            ,{' '}
            <BlogLink internal="/prizes/details/win-family-day-thorpe-park">
              Thorpe Park
            </BlogLink>
            , or{' '}
            <BlogLink internal="/prizes/details/family-getaway-to-alton-towers">
              Alton Towers
            </BlogLink>{' '}
            in the United Kingdom. These world-renowned theme parks offer a wide
            range of rides, attractions, and shows that cater to all ages. From
            Lego-themed adventures to adrenaline-pumping roller coasters,
            there's something for every member of the family. Create lasting
            memories as you explore these magical worlds together.
          </p>

          <h2>Walking Alpaca Annie in the Kent Countryside</h2>

          <p>
            Take a leisurely stroll through the picturesque{' '}
            <BlogLink internal="/prizes/details/alpaca-walking">
              Kent countryside with Alpaca Annie
            </BlogLink>
            . This unique and family-friendly experience allows you to interact
            with and walk these adorable animals. Experience authentic farm life
            as you enjoy a 45-minute animal lover’s paradise. Beginning with a
            short safety demonstration, learn fascinating facts about alpacas,
            including how to handle them. Once you’ve become acquainted with
            your alpaca friend, go for a tranquil walk together around the farm.
            You’ll even be able to feed them tasty treats and don’t forget to
            grab a selfie with your new four-legged friend!
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary1.jpg`}
            alt="Walking Alpaccas in Kent countryside"
            className="singleBlogImage"
          />

          <Separator style={{ margin: '1em 0 2.5em' }} />

          <p>
            Family-friendly vacations are all about creating those precious
            memories with your little ones. Whether you choose to stay in the
            Giraffe Manor in Nairobi, embark on a Disney Caribbean Cruise, enjoy
            an adventure holiday in North Wales, relax at the Aqua Dome Wellness
            Retreat in Tirol, experience a safari stay at Port Lympne, have fun
            at Legoland, Thorpe Park, or Alton Towers, or walk Alpaca Annie in
            the Kent countryside, enter our weekly draws for your chance to win
            our range of prizes guaranteed to satiate whatever your family is
            into!
          </p>

          <RoundedButton
            href={getCta()?.url}
            style={{ minWidth: '180px', margin: '1em 0 1.5em' }}
          >
            Enter Now
          </RoundedButton>
        </>
      </BlogArticle>
    </MainLayout>
  );
};

export default Article;
